/**
 * 客户端自动发现
 * mDNS/广播服务发现协议
 */
import dgram from 'dgram'
import { EventEmitter } from 'events'

export interface DiscoveredServer {
  id: string
  host: string
  port: number
  metadata?: Record<string, any>
  discoveredAt: number
}

export interface DiscoveryOptions {
  /** 广播端口，默认 41234 */
  broadcastPort?: number
  /** 发现间隔（ms），默认 5000 */
  interval?: number
  /** 服务标识 */
  serviceType?: string
  /** 超时（ms），默认 30000 */
  ttl?: number
}

export class ServiceDiscovery extends EventEmitter {
  private socket: dgram.Socket | null = null
  private timer: ReturnType<typeof setInterval> | null = null
  private servers = new Map<string, DiscoveredServer>()
  private options: Required<DiscoveryOptions>
  private running = false

  constructor(options?: DiscoveryOptions) {
    super()
    this.options = {
      broadcastPort: options?.broadcastPort ?? 41234,
      interval: options?.interval ?? 5000,
      serviceType: options?.serviceType ?? 'remotekit',
      ttl: options?.ttl ?? 30000,
    }
  }

  /** 开始发现 */
  start(): void {
    if (this.running) return
    this.running = true
    this.socket = dgram.createSocket({ type: 'udp4', reuseAddr: true })

    this.socket.on('message', (msg, rinfo) => {
      try {
        const data = JSON.parse(msg.toString())
        if (data.serviceType !== this.options.serviceType) return
        const server: DiscoveredServer = {
          id: data.id,
          host: rinfo.address,
          port: data.port,
          metadata: data.metadata,
          discoveredAt: Date.now(),
        }
        const isNew = !this.servers.has(server.id)
        this.servers.set(server.id, server)
        if (isNew) this.emit('discovered', server)
      } catch {}
    })

    this.socket.bind(this.options.broadcastPort, () => {
      this.socket!.setBroadcast(true)
      this.sendProbe()
    })

    this.timer = setInterval(() => {
      this.sendProbe()
      this.evictStale()
    }, this.options.interval)
  }

  /** 停止发现 */
  stop(): void {
    this.running = false
    if (this.timer) { clearInterval(this.timer); this.timer = null }
    if (this.socket) { this.socket.close(); this.socket = null }
  }

  /** 获取已发现服务器 */
  getServers(): DiscoveredServer[] {
    return [...this.servers.values()]
  }

  /** 获取活跃服务器数量 */
  get activeCount(): number {
    return this.servers.size
  }

  get isRunning(): boolean {
    return this.running
  }

  private sendProbe(): void {
    if (!this.socket) return
    const probe = Buffer.from(JSON.stringify({
      type: 'probe',
      serviceType: this.options.serviceType,
    }))
    this.socket.send(probe, 0, probe.length, this.options.broadcastPort, '255.255.255.255')
  }

  private evictStale(): void {
    const now = Date.now()
    for (const [id, server] of this.servers) {
      if (now - server.discoveredAt > this.options.ttl) {
        this.servers.delete(id)
        this.emit('lost', server)
      }
    }
  }
}
